// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { AvailabilityMessageType } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import HubspotChatLink from "@store/Library/Components/HubspotChatLink";
import SpecialOrderInfoBox from "@store/Library/Components/SpecialOrderInfoBox";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
        productState: getProductState(state, state.pages.productDetails.selectedProductId).value,
    };
};

type OwnProps = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsExpectedDeliveryStyles {
    expectedDeliveryInfosBox?: InfosBoxProps;
    expectedDeliveryTooltip?: TooltipPresentationProps;
}

export const productDetailsExpectedDeliveryStyles: ProductDetailsExpectedDeliveryStyles = {
    expectedDeliveryInfosBox: {
        type: "info",
        size: "small",
        showIcon: false,
        extendedStyles: {
            wrapper: {
                css: css`
                    font-weight: ${x => x.theme.design.typography.weight.bold};
                    margin: ${pxToRem(16)} 0;
                    width: fit-content;
                    ${breakpoint("sm")} {
                        ${x => cssProp("font-size", x.theme.design.typography.size.smaller)}
                    }
                `,
            },
        },
    },
    expectedDeliveryTooltip: {
        iconProps: {
            size: 16,
        },
    },
};

const styles = productDetailsExpectedDeliveryStyles;

const ProductDetailsExpectedDelivery: React.FC<OwnProps> = ({ productContext, isSignedIn, productState }) => {
    const { product, productInfo } = productContext;
    const { inventory, unitOfMeasure } = productInfo;
    const isVariantParent = product.isVariantParent;

    if (isVariantParent || !isSignedIn || !inventory) {
        return null;
    }

    const { inventoryAvailabilityDtos, qtyOnHand, additionalResults } = inventory;

    const availability = inventoryAvailabilityDtos?.find(
        o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
    )?.availability;

    const availabilityType = availability?.messageType;

    const isSpecialOrderProduct = availabilityType === (4 as any);

    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return <HubspotChatLink />;
    }

    const leadDate = additionalResults?.leadDate;
    const leadTimeAlternativeWarehouse = additionalResults?.leadTimeAlternativeWarehouse;

    const currentWarehouseQty = Number(inventory?.additionalResults?.currentWarehouseQty ?? 0);
    const qtyOrdered = productInfo?.qtyOrdered || 0;

    const showLeadTimeAlternativeWarehouse = qtyOnHand !== currentWarehouseQty && qtyOrdered > currentWarehouseQty;
    const displayedLeadDate = showLeadTimeAlternativeWarehouse ? leadTimeAlternativeWarehouse : leadDate;

    const estimatedArrivalLabel = `${translate("{0}:", translate("Grimco_SplitShipping_EstimatedArrival"))}`;

    return (
        <StyledWrapper>
            {(leadDate || leadTimeAlternativeWarehouse) && (
                <InfosBox {...styles.expectedDeliveryInfosBox}>
                    {`${estimatedArrivalLabel} ${displayedLeadDate}`}
                    <Tooltip
                        {...styles.expectedDeliveryTooltip}
                        text={translate("ProductDetailsExpectedDelivery_Tooltip")}
                    />
                </InfosBox>
            )}
            {isSpecialOrderProduct && <SpecialOrderInfoBox />}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProductContext(ProductDetailsExpectedDelivery)),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        icon: "house",
    },
};

export default widgetModule;
